import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import locationService from '../services/locationService';
import { useAuth } from './AuthContext';

const LocationContext = createContext(null);
const getLocationId = (location) => String(location?._id ?? location ?? '');

const toLocationObject = (location) =>
  typeof location === 'object' && location !== null && location._id
    ? location
    : location
      ? { _id: location, name: 'Location' }
      : null;

export const LocationProvider = ({ children }) => {
  const { user } = useAuth();
  const [locations, setLocations] = useState([]);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [loading, setLoading] = useState(true);

  const isSuperAdmin = user?.role === 'super_admin' || user?.isSuperAdmin;
  const isAgent = user?.role === 'agent';
  const isTeamLeader = user?.role === 'team_leader';

  useEffect(() => {
    const fetchLocations = async () => {
      if (!user) {
        localStorage.removeItem('currentLocationId');
        setCurrentLocation(null);
        setLocations([]);
        setLoading(false);
        return;
      }
      
      // Super admin: locations managed via AdminContext
      if (isSuperAdmin) {
        localStorage.removeItem('currentLocationId');
        
        if (user.selectedOrganizationId && user.availableLocations?.length > 0) {
          setLocations(user.availableLocations);
          const selectedLoc = user.availableLocations.find(l => 
            l._id === user.selectedLocationId?._id || l._id === user.selectedLocationId
          );
          if (selectedLoc) {
            setCurrentLocation(selectedLoc);
            localStorage.setItem('currentLocationId', selectedLoc._id);
          } else {
            setCurrentLocation(null);
          }
        } else {
          setLocations([]);
          setCurrentLocation(null);
        }
        setLoading(false);
        return;
      }
      
      // Agents and Team Leaders: use their assigned locationIds
      if (isAgent || isTeamLeader) {
        // Guard against stale location state from previous profile/role.
        const savedLocationId = localStorage.getItem('currentLocationId');
        setCurrentLocation(null);
        localStorage.removeItem('currentLocationId');
        const assignedLocs = user.locationIds || [];
        if (assignedLocs.length > 0) {
          setLocations(assignedLocs);
          const defaultLoc =
            assignedLocs.find(
              (l) => getLocationId(l) === String(savedLocationId),
            ) || assignedLocs[0];
          if (defaultLoc) {
            const locObj = toLocationObject(defaultLoc);
            setCurrentLocation(locObj);
            localStorage.setItem('currentLocationId', getLocationId(locObj));
          }
        } else {
          localStorage.removeItem('currentLocationId');
          setLocations([]);
          setCurrentLocation(null);
        }
        setLoading(false);
        return;
      }
      
      // Owner / TC / Front Desk: fetch from API
      try {
        const savedLocationId = localStorage.getItem('currentLocationId');
        const defaultUserLocationId = getLocationId(user.defaultLocationId);

        // Prevent stale location header from a previously logged-in profile.
        // Until we resolve the user's valid locations, avoid sending old X-Location-Id.
        localStorage.removeItem('currentLocationId');
        setCurrentLocation(null);

        const response = await locationService.getMyLocations();
        const fetchedLocations = Array.isArray(response?.data) ? response.data : [];
        setLocations(fetchedLocations);

        const defaultLocation = fetchedLocations.find(
          (location) => getLocationId(location) === String(savedLocationId),
        ) || fetchedLocations.find(
          (location) => getLocationId(location) === defaultUserLocationId,
        ) || fetchedLocations[0];

        if (defaultLocation) {
          setCurrentLocation(defaultLocation);
          localStorage.setItem('currentLocationId', getLocationId(defaultLocation));
        }
      } catch (error) {
        console.error('Failed to fetch locations:', error);
        setLocations([]);
        setCurrentLocation(null);
        localStorage.removeItem('currentLocationId');
      } finally {
        setLoading(false);
      }
    };

    fetchLocations();
  }, [user, isSuperAdmin, isAgent, isTeamLeader]);

  // Safety net: auto-select first location if locations loaded but none selected
  useEffect(() => {
    if (!loading && !isSuperAdmin && locations.length > 0 && !currentLocation) {
      const fallback = toLocationObject(locations[0]);
      setCurrentLocation(fallback);
      localStorage.setItem('currentLocationId', getLocationId(fallback));
    }
  }, [loading, isSuperAdmin, locations, currentLocation]);

  // Reconcile stale selected location against the current allowed location list.
  useEffect(() => {
    if (loading || isSuperAdmin) return;
    if (!currentLocation?._id || !locations.length) return;
    const currentId = String(currentLocation._id);
    const stillAllowed = locations.some((l) => String(l?._id ?? l) === currentId);
    if (stillAllowed) return;

    const fallback = locations[0];
    if (fallback) {
      const fallbackObj = toLocationObject(fallback);
      setCurrentLocation(fallbackObj);
      localStorage.setItem('currentLocationId', getLocationId(fallbackObj));
    }
  }, [loading, isSuperAdmin, locations, currentLocation]);

  const switchLocation = useCallback(async (locationId) => {
    const idStr = String(locationId);
    const location = locations.find((l) => String(l._id ?? l) === idStr);
    if (location) {
      try {
        await locationService.switchLocation(idStr);
        setCurrentLocation(location);
        localStorage.setItem('currentLocationId', idStr);
      } catch (error) {
        console.error('Failed to switch location:', error);
        throw error;
      }
    }
  }, [locations]);

  const value = useMemo(() => ({
    locations,
    currentLocation,
    currentLocationId: currentLocation?._id != null ? String(currentLocation._id) : null,
    switchLocation,
    loading,
    isSuperAdmin,
  }), [locations, currentLocation, switchLocation, loading, isSuperAdmin]);

  return (
    <LocationContext.Provider value={value}>
      {children}
    </LocationContext.Provider>
  );
};

export const useLocation = () => {
  const context = useContext(LocationContext);
  if (!context) {
    throw new Error('useLocation must be used within LocationProvider');
  }
  return context;
};

export default LocationContext;

